import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Bot } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { ApiError, api } from "../../api";
import { MANAGER_START_SECTIONS, useIsAdmin } from "../../lib/permissions";
import { useFactory } from "../../store";
import type { IssueDetail } from "../../types";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Field, FieldDescription, FieldLabel } from "../ui/field";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Spinner } from "../ui/spinner";

const INTAKE_START = "intake";

export function SendToSkynetDialog({ issue, onChanged }: { issue: IssueDetail; onChanged: () => void }) {
  const { layout, agentById } = useFactory();
  const admin = useIsAdmin();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [startAgentId, setStartAgentId] = useState(INTAKE_START);
  const sections = (layout?.sections ?? []).filter(
    (section) =>
      !section.detached && section.agents.length > 0 && (admin || MANAGER_START_SECTIONS.has(section.id)),
  );
  const chosen = startAgentId === INTAKE_START ? undefined : agentById.get(startAgentId);
  const delegate = useMutation({
    mutationFn: () =>
      api.delegateIssue(issue.identifier, {
        startAgentId: startAgentId === INTAKE_START ? null : startAgentId,
      }),
    onSuccess: () => {
      const name = startAgentId === INTAKE_START ? null : agentById.get(startAgentId)?.name;
      toast.success(
        name ? `Sent to Skynet — starting at the ${name}` : "Sent to Skynet — intake will pick it up",
      );
      setOpen(false);
      onChanged();
      void queryClient.invalidateQueries({ queryKey: ["tasks"] });
      void queryClient.invalidateQueries({ queryKey: ["state"] });
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Delegation failed"),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) {
          const stations = sections.flatMap((section) => section.agents);
          setStartAgentId(
            issue.currentAgentId && stations.some((station) => station.id === issue.currentAgentId)
              ? issue.currentAgentId
              : INTAKE_START,
          );
        }
        setOpen(next);
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" className="w-full" disabled={delegate.isPending}>
          {delegate.isPending && <Spinner />}
          <Bot className="size-4" /> Send to Skynet
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            delegate.mutate();
          }}
          className="grid gap-6"
        >
          <DialogHeader>
            <DialogTitle>Send to Skynet</DialogTitle>
            <DialogDescription>
              Choose where {issue.identifier} enters the line. Intake is the usual start. Pick a later station
              to run the line again from there.
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel>Start at station</FieldLabel>
            <Select value={startAgentId} onValueChange={setStartAgentId}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={INTAKE_START}>First intake station</SelectItem>
                {sections.map((section) => (
                  <SelectGroup key={section.id}>
                    <SelectLabel>{section.name}</SelectLabel>
                    {section.agents.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>
                        {agent.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
            <FieldDescription>
              {chosen
                ? `Starts at the ${chosen.name} and continues along the line from there.`
                : "Evaluated, estimated, and endorsed first. The endorser picks the lane."}
            </FieldDescription>
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={delegate.isPending}>
              {delegate.isPending && <Spinner />}
              Send
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
